import React, { useState, useMemo } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { apiEndpointsData } from '../../data/apiEndpoints';
import ApiEndpointGroup from './ApiEndpointGroup';

const ApiDocumentation = () => {
  const [searchQuery, setSearchQuery] = useState('');

  // Filter categories and endpoints based on search query
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) {
      return apiEndpointsData.categories;
    }

    const query = searchQuery.toLowerCase();

    return apiEndpointsData.categories
      .map((category) => {
        // Filter endpoints within this category
        const filteredEndpoints = category.endpoints.filter((endpoint) => {
          return (
            endpoint.path.toLowerCase().includes(query) ||
            endpoint.method.toLowerCase().includes(query) ||
            endpoint.summary.toLowerCase().includes(query) ||
            endpoint.description.toLowerCase().includes(query)
          );
        });

        // Return category with filtered endpoints if any match
        if (filteredEndpoints.length > 0) {
          return {
            ...category,
            endpoints: filteredEndpoints,
          };
        }

        // Also include category if category name matches
        if (
          category.name.toLowerCase().includes(query) ||
          category.description.toLowerCase().includes(query)
        ) {
          return category;
        }

        return null;
      })
      .filter(Boolean); // Remove null entries
  }, [searchQuery]);

  const totalEndpoints = useMemo(() => {
    return filteredCategories.reduce(
      (sum, category) => sum + category.endpoints.length,
      0
    );
  }, [filteredCategories]);

  return (
    <div className="api-docs-container">
      {/* Header */}
      <div style={{ marginBottom: 'var(--space-xl)' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 'var(--space-sm)' }}>
          API Reference
        </h2>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.95rem' }}>
          Complete documentation for all core business API endpoints.
          {!searchQuery && (
            <span> Total: <strong>{apiEndpointsData.categories.reduce((sum, cat) => sum + cat.endpoints.length, 0)}</strong> endpoints across <strong>{apiEndpointsData.categories.length}</strong> categories.</span>
          )}
        </p>
      </div>

      {/* Search Bar */}
      <div className="api-search-bar">
        <div style={{ position: 'relative' }}>
          <MagnifyingGlassIcon
            style={{
              position: 'absolute',
              left: 'var(--space-md)',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 20,
              height: 20,
              color: 'var(--color-text-tertiary)',
              pointerEvents: 'none'
            }}
          />
          <input
            type="text"
            placeholder="Search endpoints by path, method, or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: 'var(--space-md) var(--space-md) var(--space-md) 48px',
              background: 'var(--color-bg-primary)',
              border: '1px solid var(--color-border-primary)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-text-primary)',
              fontSize: '0.95rem',
              outline: 'none',
              transition: 'all var(--transition-fast)'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--color-accent-primary)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--color-border-primary)';
            }}
          />
        </div>
        {searchQuery && (
          <div style={{ marginTop: 'var(--space-sm)', fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
            Found <strong>{totalEndpoints}</strong> endpoint{totalEndpoints !== 1 ? 's' : ''} in <strong>{filteredCategories.length}</strong> categor{filteredCategories.length !== 1 ? 'ies' : 'y'}
            <button
              onClick={() => setSearchQuery('')}
              style={{
                marginLeft: 'var(--space-md)',
                padding: '4px 12px',
                background: 'transparent',
                border: '1px solid var(--color-border-primary)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--color-accent-primary)',
                cursor: 'pointer',
                fontSize: '0.8rem',
                transition: 'all var(--transition-fast)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'var(--color-bg-elevated)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
              }}
            >
              Clear search
            </button>
          </div>
        )}
      </div>

      {/* Categories */}
      {filteredCategories.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
          {filteredCategories.map((category, index) => (
            <ApiEndpointGroup
              key={category.id}
              category={category}
              initialExpanded={index === 0 && !searchQuery} // Expand first category by default when not searching
            />
          ))}
        </div>
      ) : (
        <div style={{
          padding: 'var(--space-2xl)',
          textAlign: 'center',
          background: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border-primary)',
          borderRadius: 'var(--radius-lg)'
        }}>
          <MagnifyingGlassIcon style={{ width: 48, height: 48, color: 'var(--color-text-tertiary)', margin: '0 auto var(--space-md)' }} />
          <h3 style={{ fontSize: '1.1rem', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-sm)' }}>
            No endpoints found
          </h3>
          <p style={{ color: 'var(--color-text-tertiary)', fontSize: '0.9rem' }}>
            Try a different search term or clear the search to see all endpoints.
          </p>
        </div>
      )}

      {/* Authentication Note */}
      <div style={{
        marginTop: 'var(--space-2xl)',
        padding: 'var(--space-lg)',
        background: 'rgba(59, 130, 246, 0.1)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        borderRadius: 'var(--radius-lg)'
      }}>
        <h4 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 'var(--space-sm)' }}>
          Authentication
        </h4>
        <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', lineHeight: '1.6', marginBottom: 'var(--space-sm)' }}>
          Most API endpoints require authentication using JWT tokens. Include the token in the Authorization header:
        </p>
        <code style={{
          display: 'block',
          padding: 'var(--space-md)',
          background: 'var(--color-bg-primary)',
          border: '1px solid var(--color-border-primary)',
          borderRadius: 'var(--radius-md)',
          fontFamily: 'var(--font-mono)',
          fontSize: '0.85rem',
          color: 'var(--color-accent-primary)'
        }}>
          Authorization: Bearer &lt;your-jwt-token&gt;
        </code>
        <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginTop: 'var(--space-sm)' }}>
          Obtain a token by calling the <code style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-accent-secondary)' }}>POST /api/auth/login</code> endpoint.
        </p>
      </div>
    </div>
  );
};

export default ApiDocumentation;
